import React, { useState } from 'react';
import useApplyOrderDraft from '../../hooks/useApplyOrderDraft';

/**
 * Renders a build_order_draft "ok" payload as a structured card instead of
 * plain text — the chat-visible counterpart to the chart preview that
 * AiOrderDraftListener applies from the same Pusher event.
 */
const Row = ({ label, value, colors, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
    <span style={{ opacity: 0.65, fontSize: '13px' }}>{label}</span>
    <span style={{ fontWeight: strong ? 600 : 500, fontSize: '14px', color: strong ? colors.white : undefined }}>
      {value}
    </span>
  </div>
);

export default function OrderDraftCard({ draft, colors }) {
  const applyDraft = useApplyOrderDraft();
  const [added, setAdded] = useState(false);
  const order = draft?.order;
  if (!order) return null;

  const isBuy = order.side !== 'sell';
  const sideColor = isBuy ? '#22c55e' : '#ef4444';
  const border = colors.borderLight || colors.border;

  const handleAddToChart = () => {
    applyDraft(draft);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: 10,
        padding: '12px 14px',
        margin: '6px 0 4px',
        background: colors.bgPrimary,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: sideColor,
              border: `1px solid ${sideColor}`,
              borderRadius: 4,
              padding: '2px 6px',
            }}
          >
            {order.side}
          </span>
          {order.symbol ? (
            <span style={{ fontWeight: 650, fontSize: '15px' }}>{order.symbol}</span>
          ) : null}
          <span style={{ opacity: 0.6, fontSize: '13px', textTransform: 'capitalize' }}>{order.orderType}</span>
        </div>
        <span
          role="button"
          tabIndex={0}
          onClick={handleAddToChart}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAddToChart(); }}
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: added ? '#22c55e' : colors.primary,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {added ? 'Added ✓' : 'Add to chart'}
        </span>
      </div>

      {order.symbol ? (
        <Row label="Symbol" value={order.symbol} colors={colors} strong />
      ) : null}
      <Row label="Quantity" value={order.quantity} colors={colors} strong />
      {order.sizingMode === 'risk' && Number.isFinite(Number(order.sizingValue)) && (
        <Row label="Risk" value={`$${Number(order.sizingValue).toFixed(2)}`} colors={colors} />
      )}
      {order.sizingMode === 'amount' && Number.isFinite(Number(order.sizingValue)) && (
        <Row label="Amount" value={`$${Number(order.sizingValue).toFixed(2)}`} colors={colors} />
      )}
      {order.orderType === 'limit' && (
        <Row label="Limit Price" value={`$${Number(order.limitPrice).toFixed(2)}`} colors={colors} />
      )}

      {order.includeBracket && Number.isFinite(order.stopLoss) && (
        <Row label="Stop Loss" value={`$${Number(order.stopLoss).toFixed(2)}`} colors={colors} />
      )}

      {order.includeBracket && order.takeProfits?.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {order.takeProfits.map((tp, i) => (
            <Row
              key={i}
              label={`Take Profit ${order.takeProfits.length > 1 ? i + 1 : ''}`.trim()}
              value={`$${Number(tp.price).toFixed(2)}${Number.isFinite(tp.percentage) ? ` (${tp.percentage}%)` : ''}`}
              colors={colors}
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.55 }}>
        Draft only — not submitted.
      </div>
    </div>
  );
}
